'use client';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Sidebar } from 'primereact/sidebar';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { formatToIndianCurrency } from '@/_config';

const calcDiscount = (price, mrp) => {
    if (!mrp || mrp <= price) return { discountPct: 0 };
    return { discountPct: Math.round(((mrp - price) / mrp) * 100) };
};

const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const update = () => setIsMobile(window.innerWidth < 768);
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);
    return isMobile;
};

const ProductDetailsBody = ({ item, onNavigate, showDragPill }) => {
    const { discountPct } = calcDiscount(item.price, item.mrp);
    const savedAmount = item.mrp && item.mrp > item.price
        ? (item.mrp - item.price) * (item.quantity ?? 1)
        : 0;
    const itemTotal = (item.price ?? 0) * (item.quantity ?? 1);

    return (
        <>
            {/* Drag-handle pill — visual affordance for mobile swipe (desktop only;
                the mobile bottom sheet already has its own real drag handle) */}
            {showDragPill && (
                <div className="flex justify-content-center mb-1">
                    <div style={{ width: '2.5rem', height: '4px', borderRadius: '999px', background: '#cbd5e1' }} />
                </div>
            )}
            <div className="flex flex-column gap-3 pb-5">
                {/* Image */}
                <div
                    className="border-round-lg overflow-hidden"
                    style={{ width: '100%', height: '220px', background: '#f8f8f8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                    {item.coverImage ? (
                        <img
                            src={item.coverImage}
                            alt={item.title}
                            style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '0.5rem' }}
                        />
                    ) : (
                        <i className="pi pi-image text-300" style={{ fontSize: '3rem' }} />
                    )}
                </div>

                {/* Title */}
                <p className="m-0 text-900 font-semibold text-base" style={{ lineHeight: '1.5' }}>{item.title}</p>

                {/* Price box — only if price is available */}
                {item.price > 0 && (
                    <div className="border-round-lg p-3" style={{ background: '#f8fffe', border: '1px solid #ccfbf1' }}>
                        <div className="flex align-items-center gap-2 flex-wrap mb-2">
                            <span className="font-bold text-teal-600" style={{ fontSize: '1.3rem' }}>
                                {formatToIndianCurrency(item.price)}
                            </span>
                            {item.mrp && item.mrp > item.price && (
                                <span className="text-400 text-sm line-through">{formatToIndianCurrency(item.mrp)}</span>
                            )}
                            {discountPct > 0 && (
                                <span className="text-xs font-bold border-round px-2 py-1" style={{ background: '#16a34a', color: 'white' }}>
                                    {discountPct}% OFF
                                </span>
                            )}
                        </div>
                        <div className="flex flex-column gap-1">
                            {item.mrp && item.mrp > item.price && (
                                <div className="flex justify-content-between text-sm">
                                    <span style={{ color: '#1bb8a7' }}>MRP</span>
                                    <span style={{ color: '#1bb8a7' }}>{formatToIndianCurrency(item.mrp)}</span>
                                </div>
                            )}
                            <div className="flex justify-content-between text-sm">
                                <span style={{ color: '#1bb8a7' }}>Selling Price</span>
                                <span className="font-medium" style={{ color: '#1bb8a7' }}>{formatToIndianCurrency(item.price)}</span>
                            </div>
                            {savedAmount > 0 && (
                                <div className="flex justify-content-between text-sm font-semibold" style={{ color: '#16a34a' }}>
                                    <span>You Save</span>
                                    <span>{formatToIndianCurrency(savedAmount)}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Quantity summary */}
                {item.quantity > 0 && (
                    <div className="border-round-lg p-3 surface-50 border-1 border-100">
                        <p className="m-0 mb-2 text-xs font-bold text-500 uppercase" style={{ letterSpacing: '0.05em' }}>
                            Summary
                        </p>
                        <div className="flex justify-content-between text-sm mb-1">
                            <span className="text-500">Quantity</span>
                            <span className="text-900 font-semibold">{item.quantity}</span>
                        </div>
                        {item.price > 0 && (
                            <div className="flex justify-content-between text-sm mb-1">
                                <span className="text-500">Price × {item.quantity}</span>
                                <span className="text-900 font-semibold">{formatToIndianCurrency(itemTotal)}</span>
                            </div>
                        )}
                        {savedAmount > 0 && (
                            <div className="flex justify-content-between text-sm" style={{ color: '#16a34a' }}>
                                <span>Total Saved</span>
                                <span className="font-semibold">{formatToIndianCurrency(savedAmount)}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Trust badges */}
                <div className="flex gap-3 flex-wrap">
                    {[
                        { icon: 'pi-shield', label: 'Genuine Product' },
                        { icon: 'pi-undo', label: 'Easy Returns' },
                        { icon: 'pi-truck', label: 'Fast Delivery' },
                    ].map(({ icon, label }) => (
                        <div key={label} className="flex align-items-center gap-1 text-xs text-500">
                            <i className={`pi ${icon} text-teal-500`} />
                            <span>{label}</span>
                        </div>
                    ))}
                </div>

                {/* Navigation — only when slug is available */}
                {item.slug && onNavigate && (
                    <>
                        <Divider className="my-1" />
                        <Button
                            label="View Full Details"
                            icon="pi pi-arrow-right"
                            iconPos="right"
                            className="w-full border-0 text-white font-semibold"
                            style={{ background: '#1bb8a7' }}
                            onClick={() => onNavigate(item.slug)}
                        />
                    </>
                )}
            </div>
        </>
    );
};

const SidebarHeader = ({ onClose }) => (
    <div className="flex align-items-center justify-content-between w-full">
        <span className="font-bold" style={{ fontSize: '1rem', color: 'var(--primary-color)' }}>Product Details</span>
        <div className="flex align-items-center gap-2">
            <span
                className="flex align-items-center gap-1"
                style={{ fontSize: '0.7rem', background: '#1bb8a7', borderRadius: '999px', padding: '0.2rem 0.6rem', color: 'white' }}
            >
                <i className="pi pi-arrow-down" style={{ fontSize: '0.65rem' }} />
                Scroll for details
            </span>
            {onClose && (
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                        background: 'var(--surface-100)', border: 'none', cursor: 'pointer',
                    }}
                >
                    <i className="pi pi-times" style={{ fontSize: '0.75rem', color: '#64748b' }} />
                </button>
            )}
        </div>
    </div>
);

export const ProductSidebar = ({ item, visible, onHide, onNavigate }) => {
    const isMobile = useIsMobile();
    if (!item) return null;

    if (isMobile) {
        if (!visible) return null;
        return createPortal(
            <>
                <style>{`
                    @keyframes ennix-product-sheet-up {
                        from { transform: translateY(100%); }
                        to   { transform: translateY(0); }
                    }
                `}</style>
                <div
                    onClick={onHide}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1400 }}
                />
                <div style={{
                    position: 'fixed', bottom: 0, left: 0, right: 0,
                    background: '#fff', borderRadius: '18px 18px 0 0',
                    zIndex: 1401,
                    animation: 'ennix-product-sheet-up 0.28s cubic-bezier(0.32,0.72,0,1)',
                    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                    maxHeight: '90vh', overflowY: 'auto',
                }}>
                    {/* Drag handle */}
                    <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 2 }}>
                        <div style={{ width: 36, height: 4, borderRadius: 99, background: '#e2e8f0' }} />
                    </div>
                    <div style={{ background: '#fff', borderBottom: '1px solid var(--surface-border)', padding: '0.85rem 1rem' }}>
                        <SidebarHeader onClose={onHide} />
                    </div>
                    <div style={{ padding: '1rem' }}>
                        <ProductDetailsBody item={item} onNavigate={onNavigate} showDragPill={false} />
                    </div>
                </div>
            </>,
            document.body
        );
    }

    return (
        <Sidebar
            visible={visible}
            position="right"
            onHide={onHide}
            style={{ width: '360px' }}
            pt={{ header: { style: { background: '#fff', borderBottom: '1px solid var(--surface-border)', padding: '0.85rem 1rem' } } }}
            header={<SidebarHeader />}
        >
            <ProductDetailsBody item={item} onNavigate={onNavigate} showDragPill />
        </Sidebar>
    );
};
